import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {PagedResponse} from '@app/asset';

import {forkJoin, from, Observable, ReplaySubject} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {deserialize, deserializeArray} from '../serializable';
import {Tag} from './models';

@Injectable({
    providedIn: 'root',
})
export class TagService {
    public tagUpdate: ReplaySubject<Tag>;
    public tags: ReplaySubject<Tag[]>;
    private cache: Map<string, Tag>;

    constructor(private http: HttpClient) {
        this.tagUpdate = new ReplaySubject<Tag>();
        this.cache = new Map<string, Tag>();
        this.tags = new ReplaySubject<Tag[]>(1);
    }

    getTagsByIds(tagIds: string[]): Observable<Tag[]> {
        const promise = new Promise<Tag[]>((resolve, reject) => {
            const tags = tagIds.map((id) => this.cache.get(id));
            resolve(tags);
        });

        return from(promise);
    }

    resolveName(name: string): Observable<Tag> {
        const url = '/api/tag/';
        const options = {
            name: name,
        };
        return this.http.post<Tag>(url, options).pipe(
            map((tag) => deserialize(Tag, tag)),
            tap((tag) => {
                const cacheLength = this.cache.size;
                this.cache.set(tag.id, tag);
                if (cacheLength !== this.cache.size) {
                    this.tags.next(Array.from(this.cache.values()));
                }
            }),
        );
    }

    resolveGuids(guids: string[]): Observable<Tag[]> {
        const url = '/api/tag/resolve/';
        const options = {
            guids
        };
        return this.http.post<Tag[]>(url, options).pipe(
            map((tags) => deserializeArray(Tag, tags)),
            tap((tags) => {
                const cacheLength = this.cache.size;

                for (const tag of tags) {
                    this.cache.set(tag.id, tag);
                }

                if (cacheLength !== this.cache.size) {
                    this.tags.next(Array.from(this.cache.values()));
                }
            }),
        );
    }

    list(
        pageIndex: number = 0,
        query: string = '',
        categories: string[] = [],
        unusedOnly: boolean = false,
        ordering: string = null,
    ): Observable<PagedResponse> {
        let filterQuery = new HttpParams();
        filterQuery = filterQuery.set('page', String(pageIndex + 1));
        categories.map((category) => (filterQuery = filterQuery.set('c', category)));
        if (query) {
            filterQuery = filterQuery.set('q', query);
        }
        if (unusedOnly) {
            filterQuery = filterQuery.set('u', '1');
        }
        if (ordering) {
            filterQuery = filterQuery.set('ordering', ordering);
        }

        return this.http.get<PagedResponse>(`/api/tag/?${filterQuery.toString()}`).pipe(
            map((pagedResults) => {
                pagedResults.results = deserializeArray(Tag, pagedResults.results);
                return pagedResults;
            }),
        );
    }

    autocomplete(
        query: string,
        categories: string[] = null,
        excludeCategories: string[] = null,
    ): Observable<Tag[]> {
        let url = `/api/tag/?q=${query}`;
        if (categories) {
            url += '&' + categories.map((category) => `c=${category}`).join('&');
        }
        if (excludeCategories) {
            url += '&' + excludeCategories.map((category) => `cx=${category}`).join('&');
        }

        return this.http.get<PagedResponse>(url).pipe(
            map((pagedResults) => deserializeArray(Tag, pagedResults.results)),
            map((tags) => {
                // sort by artist
                tags.sort((a, b) => (a.category === 'artist' ? -1 : 1));
                return tags;
            }),
        );
    }

    delete(tags: Tag[]): Observable<any> {
        const observables = tags.map((tag) => {
            const url = `/api/tag/${tag.id}/`;
            return this.http.delete(url);
        });

        return forkJoin(observables).pipe(
            tap(() => {
                tags.forEach((tag) => this.cache.delete(tag.id));
                this.tags.next(Array.from(this.cache.values()));
            }),
        );
    }

    update(tag: Tag) {
        const url = `/api/tag/${tag.id}/`;
        const options = {
            name: tag.name,
            withCredentials: true,
        };
        return this.http.put<Tag>(url, options).pipe(
            map((tag) => deserialize(Tag, tag)),
            tap((tag) => {
                this.tagUpdate.next(tag);
            }),
        );
    }

    merge(headTagId: string, tagIds: string[]): Observable<Tag> {
        const url = `/api/tag/${headTagId}/merge/`;
        const options = {
            tag_ids: tagIds,
            withCredentials: true,
        };
        return this.http.post(url, options).pipe(
            map((tag) => deserialize(Tag, tag)),
            tap((tag) => {
                tagIds.forEach((tagId) => this.cache.delete(tagId));
                this.cache.set(tag.id, tag);
                this.tags.next(Array.from(this.cache.values()));
                this.tagUpdate.next(tag);
            }),
        );
    }
}
